import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { AutocompleteSearch, Button } from 'components/UIElements'
import { MODAL_CLASSES_MAP } from 'utils/constants'
import STRINGS from 'utils/strings'
import { generateNotyMessage } from 'utils/request'
import { doVisitCyclesSpanFullStudyDuration } from '../../utils/visitDayUtils'
import VisitCycleModal from './VisitCycleModal'

const VisitCycleScheduleAndDaySelector = props => {
  const {
    disabled,
    isEdit,
    selectedVisitScheduleId,
    setSelectedVisitScheduleId,
    selectedCycleId,
    setCycleId,
    visitScheduleOptions,
    studyVisitSchedule,
    onCreateNewCycle,
    onEditCycle,
    studyID,
    openModal,
    closeModal,
    updateVisitCycle,
    templateVisitCycle,
    templateVisit,
    fetchVisitSchedules,
    visitCycleCreated,
    setSelectedVisitCycle,
    resetSelectedVisitCycle,
    isReadOnlyState,
    studyDuration,
    showError,
  } = props

  const [searchValue, setSearchValue] = useState('')

  const formatPhases = (phases = []) => {
    if (!phases.length) return []
    return phases[0].cycles.map(({ id, name, is_dummy }) => ({ key: id, text: name, isDummy: is_dummy }))
  }

  const ids = selectedVisitScheduleId?.split('_')

  const getPhasesList = () => {
    if (!selectedVisitScheduleId) return []
    return studyVisitSchedule.filter(({ id }) => id === ids?.[0])[0]?.phases
  }

  const phasesList = getPhasesList()

  const configuredCycles = useMemo(
    () => phasesList?.[0]?.cycles?.filter(cycle => !cycle?.is_dummy) || [],
    [phasesList],
  )

  const cyclesSpanFullStudyDuration = useMemo(
    () => doVisitCyclesSpanFullStudyDuration(configuredCycles, studyDuration),
    [configuredCycles, studyDuration],
  )

  const armScheduleId = ids?.[0] || selectedVisitScheduleId

  useEffect(() => {
    if (!isEdit || !phasesList?.length || selectedCycleId) return

    const list = formatPhases(phasesList) || []
    const cycleIdFromUrl = ids?.[2]
    const savedCycleId = templateVisit?.study_phase_cycle_id
    const resolvedCycleId = cycleIdFromUrl || savedCycleId

    if (armScheduleId && selectedVisitScheduleId?.includes('_')) {
      setSelectedVisitScheduleId(armScheduleId)
    }

    const cycle = list.filter(({ isDummy }) => !isDummy).find(item => item.key === resolvedCycleId)
    const fullCycle = phasesList?.[0]?.cycles?.find(c => c.id === cycle?.key)

    if (fullCycle) {
      setCycleId(fullCycle.id)
      setSelectedVisitCycle(fullCycle)
    }
  }, [isEdit, phasesList, templateVisit?.study_phase_cycle_id, selectedCycleId])

  const onAddNewCycle = async cycleData => {
    // TODO: change phases to be the selected phase, for now the first phase will be the default
    const phaseID = phasesList?.[0]?.id
    await onCreateNewCycle({ studyID, scheduleID: armScheduleId, phaseID, cycleData })
    await fetchVisitSchedules(studyID)
  }
  const onEditExistingCycle = async cycleData => {
    // TODO: change phases to be the selected phase, for now the first phase will be the default
    const phaseID = phasesList?.[0]?.id

    if (selectedCycleId) {
      await onEditCycle({ studyID, scheduleID: armScheduleId, phaseID, cycleID: selectedCycleId, cycleData })
      await fetchVisitSchedules(studyID)
    }
  }

  useEffect(() => {
    const cycle = visitCycleCreated?.payload?.message?.[0]
    if (!Object.keys(cycle || {})?.length) return

    if (!selectedCycleId) {
      setCycleId(cycle?.id)
      setSelectedVisitCycle(cycle)
    }
  }, [visitCycleCreated, selectedCycleId])

  const getAvailableCycles = isEdit => {
    if (isEdit) {
      return formatPhases(phasesList).filter(option => option.key !== selectedCycleId && !option.isDummy)
    }
    return formatPhases(phasesList).filter(option => !option.isDummy)
  }

  const onAddConfigVisitCycle = (cycleName, isEdit = false) => {
    if (!isEdit && cyclesSpanFullStudyDuration) {
      showError?.({
        text: generateNotyMessage(STRINGS.visitCyclesSpanFullStudyDuration, false),
      })
      return
    }

    let cycleData = {}
    const cyclesList = phasesList[0]?.cycles
    let lastCycleData = cyclesList?.length > 1 ? cyclesList?.slice(-1)[0] : {}
    if (isEdit) {
      cycleData = cyclesList.filter(cycle => cycle.id === selectedCycleId).flat()[0]
      if (cyclesList.length > 1) {
        const cycleDataIndex = cyclesList?.findIndex(cycle => cycle.id === selectedCycleId)
        lastCycleData = cyclesList[cycleDataIndex - 1]
      }
    }
    openModal({
      className: MODAL_CLASSES_MAP.spacious,
      content: (
        <VisitCycleModal
          cycleName={cycleName || cycleData?.name}
          disabled={disabled}
          isEdit={isEdit}
          cycleData={cycleData}
          phasesList={phasesList}
          lastCycleData={lastCycleData}
          onAddNewCycle={onAddNewCycle}
          onEditExistingCycle={onEditExistingCycle}
          onClose={closeModal}
          allCycles={getAvailableCycles(isEdit)}
          setSearchValue={setSearchValue}
          isReadOnlyState={isReadOnlyState}
          studyDuration={studyDuration}
          showError={showError}
        />
      ),
      noButtons: true,
      hideClose: true,
      closeOnBackgroundClick: false,
    })
  }

  const renderTopItem = (list, options = {}) => {
    const { disableItemCreation = true, disableAddAsNew = false } =
      typeof options === 'boolean' ? { disableItemCreation: options } : options

    return searchTerm => {
      if (!list.find(item => item.text === searchTerm)) {
        if (disableAddAsNew) {
          return (
            <>
              <li className='top-item disabled' aria-disabled='true'>
                <i className='fas fa-plus' />
                Add As New
              </li>
              <li className='visit-cycle-full-duration-msg visit-cycle-add-blocked-msg'>
                {STRINGS.visitCyclesSpanFullStudyDuration}
              </li>
            </>
          )
        }
        if (disableItemCreation) {
          return <li className='no-match'>No Matching</li>
        }
        return (
          <li
            className='top-item'
            onClick={() => {
              onAddConfigVisitCycle(searchTerm)
            }}>
            <i className='fas fa-plus' />
            Add As New
          </li>
        )
      }
    }
  }

  const getVisitCycleTopItemOptions = () => {
    if (cyclesSpanFullStudyDuration) {
      return { disableItemCreation: false, disableAddAsNew: true }
    }
    return { disableItemCreation: !selectedVisitScheduleId }
  }

  const getCyclesList = () => {
    const list = formatPhases(phasesList)
    return list?.filter(item => !item?.isDummy)
  }

  const activeArm = visitScheduleOptions.find(({ key }) => key === ids?.[0])

  const activeCycle = formatPhases(phasesList).find(({ key }) => key === selectedCycleId)

  return (
    <div className='visit-schedule-and-day-selector'>
      <label className='label-small' htmlFor='visit-schedule-selection'>
        STUDY ARM
      </label>
      <div>
        <AutocompleteSearch
          loading={false}
          disabled={disabled || isReadOnlyState}
          renderTopItem={renderTopItem(visitScheduleOptions)}
          checked={activeArm}
          toggleItem={option => {
            if (selectedVisitScheduleId === option) {
              setSelectedVisitScheduleId(undefined)
            } else {
              setSelectedVisitScheduleId(option)
            }
          }}
          id='study-arm'
          placeholder={activeArm?.text || 'Search for study arm...'}
          list={visitScheduleOptions}
        />
      </div>
      <label className='label-small' htmlFor='visit-cycle'>
        VISIT CYCLE (OPTIONAL)
      </label>
      <div className={`visit-cycle-autocomplete ${disabled || !activeArm?.key ? 'disabled' : ''}`}>
        <AutocompleteSearch
          loading={false}
          disabled={disabled || !activeArm?.key || isReadOnlyState}
          renderTopItem={renderTopItem(getCyclesList(), getVisitCycleTopItemOptions())}
          checked={activeCycle}
          toggleItem={option => {
            if (selectedCycleId === option) {
              setCycleId(undefined)
              resetSelectedVisitCycle()
            } else {
              setCycleId(option)

              const cycle = phasesList?.[0].cycles?.find(cycle => cycle?.id === option)
              if (!!Object.keys(cycle || {})?.length) {
                setSelectedVisitCycle(cycle)
              }
            }

            setSearchValue('')
          }}
          id='visit-cycle'
          placeholder={
            searchValue || (activeCycle && !activeCycle?.isDummy ? activeCycle?.text : 'Search for visit cycle...')
          }
          list={getCyclesList()}
          setSearchValue={setSearchValue}
        />
        <Button
          className='inline flexed edit-cycle-button'
          disabled={!selectedCycleId}
          onClick={() => onAddConfigVisitCycle('', true)}
          link
          icon='fas fa-pencil-alt'
        />
      </div>
    </div>
  )
}

VisitCycleScheduleAndDaySelector.propTypes = {
  disabled: PropTypes.bool,
  isEdit: PropTypes.bool,
  selectedVisitScheduleId: PropTypes.string,
  setSelectedVisitScheduleId: PropTypes.func,
  visitScheduleOptions: PropTypes.arrayOf(PropTypes.object),
  visitScheduleInitialData: PropTypes.shape({
    visitOptions: PropTypes.array,
  }),
}

export default VisitCycleScheduleAndDaySelector
